/**
 * apps/admin — every shipped screen behind one session gate.
 *
 * Routes are gated twice: `RequireCapability` keeps a signed-in user off a screen
 * their role has no capability for, and the API enforces the same capability
 * independently on every request. The client-side gate is a courtesy; the server
 * one is the control.
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import type { AdminCapability } from '@navoiy/domain';
import { SessionProvider, useSession } from './auth/SessionContext.js';
import { LocaleProvider, useI18n } from './i18n/LocaleContext.jsx';
import { AppShell } from './components/AppShell.jsx';
import { LoginScreen } from './screens/LoginScreen.jsx';
import { DashboardScreen } from './screens/DashboardScreen.jsx';
import { MahallasScreen } from './screens/MahallasScreen.jsx';
import { AppellantDetailScreen, AppellantsScreen } from './screens/AppellantsScreen.jsx';
import { StatsScreen } from './screens/StatsScreen.jsx';
import { ExecutorsScreen } from './screens/ExecutorsScreen.jsx';
import { ReportsScreen } from './screens/ReportsScreen.jsx';
import { AuditScreen } from './screens/AuditScreen.jsx';
import { InboxScreen } from './screens/InboxScreen.jsx';
import { DetailScreen } from './screens/DetailScreen.jsx';
import { ReceptionScreen } from './screens/ReceptionScreen.jsx';
import { AiScreen } from './screens/AiScreen.jsx';
import { DirectoryScreen } from './screens/DirectoryScreen.jsx';
import { DevicesScreen } from './screens/DevicesScreen.jsx';
import { UsersScreen } from './screens/UsersScreen.jsx';
import { EmptyState, Spinner } from './ui/index.jsx';

function RequireCapability({
  capability,
  children,
}: {
  capability: AdminCapability;
  children: React.JSX.Element;
}): React.JSX.Element {
  const { allows } = useSession();
  const { t } = useI18n();
  if (!allows(capability)) return <EmptyState message={t('admin.error.forbidden')} />;
  return children;
}

/**
 * The first screen a role can actually reach — never a 403 as a landing page.
 *
 * §14.8: `analytics.read` leads, so every role that has the dashboard lands on
 * it. `content_editor` has neither `analytics.read` nor `inbox.list` and falls
 * through to the directory, which is its whole job.
 */
function LandingRedirect(): React.JSX.Element {
  const { allows } = useSession();
  if (allows('analytics.read')) return <Navigate to="/" replace />;
  if (allows('inbox.list')) return <Navigate to="/murojaats" replace />;
  if (allows('directory.read')) return <Navigate to="/directory" replace />;
  if (allows('device.read')) return <Navigate to="/devices" replace />;
  if (allows('user.manage')) return <Navigate to="/users" replace />;
  return <Navigate to="/murojaats" replace />;
}

/**
 * `/` is the dashboard for a role that may read analytics, and a redirect for
 * one that may not.
 *
 * Written as its own component rather than as a `RequireCapability` wrapper
 * because the "no" branch must REDIRECT rather than render a forbidden notice:
 * `/` is where the panel lands after sign-in, and a `content_editor` must not
 * be greeted by an error on a screen they never asked for. `LandingRedirect`
 * only runs here when `analytics.read` is absent, so it cannot bounce back to
 * `/` and loop.
 */
function HomeRoute(): React.JSX.Element {
  const { allows } = useSession();
  return allows('analytics.read') ? <DashboardScreen /> : <LandingRedirect />;
}

function AuthenticatedApp(): React.JSX.Element {
  const { status } = useSession();
  const { t } = useI18n();

  if (status === 'loading') return <Spinner label={t('admin.common.loading')} />;
  if (status === 'anonymous') return <LoginScreen />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<HomeRoute />} />
        <Route
          path="/mahallas"
          element={
            <RequireCapability capability="analytics.read">
              <MahallasScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/murojaats"
          element={
            <RequireCapability capability="inbox.list">
              <InboxScreen />
            </RequireCapability>
          }
        />
        {/*
          §14.8 screen 4 — BEFORE `/murojaats/:murojaatId`, and the order is
          load-bearing: react-router would otherwise match `new` as a murojaat
          id and answer the reception form with a 404 detail screen.
        */}
        <Route
          path="/murojaats/new"
          element={
            <RequireCapability capability="appeal.create">
              <ReceptionScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/murojaats/:murojaatId"
          element={
            <RequireCapability capability="appeal.read">
              <DetailScreen />
            </RequireCapability>
          }
        />
        {/*
          §14.8 round B. The list and the detail are gated on DIFFERENT
          capabilities on purpose (§14.2.6): seeing that a person has filed
          three times is `appellant.list`; opening the file that says which
          three is `appellant.read`, and that read is audit-logged.
        */}
        <Route
          path="/appellants"
          element={
            <RequireCapability capability="appellant.list">
              <AppellantsScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/appellants/:appellantId"
          element={
            <RequireCapability capability="appellant.read">
              <AppellantDetailScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/stats"
          element={
            <RequireCapability capability="analytics.read">
              <StatsScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/executors"
          element={
            <RequireCapability capability="analytics.staff">
              <ExecutorsScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireCapability capability="analytics.export">
              <ReportsScreen />
            </RequireCapability>
          }
        />
        {/*
          §14.8 screen 8. `ai.read` and not `analytics.read`: three of the
          board's four blocks are SQL a `viewer` could safely read, but the
          fourth aggregates machine output derived from appeal text, and
          §14.4.2 governs every `murojaat_ai` field like `body`.
        */}
        <Route
          path="/ai"
          element={
            <RequireCapability capability="ai.read">
              <AiScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/audit"
          element={
            <RequireCapability capability="audit.read">
              <AuditScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/directory"
          element={
            <RequireCapability capability="directory.read">
              <DirectoryScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/devices"
          element={
            <RequireCapability capability="device.read">
              <DevicesScreen />
            </RequireCapability>
          }
        />
        <Route
          path="/users"
          element={
            <RequireCapability capability="user.manage">
              <UsersScreen />
            </RequireCapability>
          }
        />
        <Route path="*" element={<LandingRedirect />} />
      </Route>
    </Routes>
  );
}

export function App(): React.JSX.Element {
  return (
    <LocaleProvider>
      <SessionProvider>
        <BrowserRouter>
          <AuthenticatedApp />
        </BrowserRouter>
      </SessionProvider>
    </LocaleProvider>
  );
}
